import React from 'react';
import styled from 'styled-components';

import { RudderstackMark, RudderstackWordmark } from '@ui-library/icons';

/** `mark` draws the icon, `wordmark` draws the letters, `full` draws both. */
type RudderstackLogoVariant = 'mark' | 'wordmark' | 'full';

/** RudderstackMark pads its glyph inside a 40x40 box. Crop to the ink so `size` means drawn height. */
const MARK_INK_VIEW_BOX = '6.1026 6 27.7928 28';

/** Ink aspect ratios of the two glyph sets — width divided by height. */
const MARK_ASPECT_RATIO = 0.9926;
const WORDMARK_ASPECT_RATIO = 10.2972;

/** Lockup proportions, measured off the previous RudderStack lockup. */
const LOCKUP_WORDMARK_SCALE = 0.46;
const LOCKUP_GAP_SCALE = 0.29;

interface RudderstackLogoProps {
  variant?: RudderstackLogoVariant;
  /** Rendered height in px. The width follows the glyph aspect ratio. */
  size?: number;
  /** Tint. Defaults to the brand blue — pass a theme token on a coloured surface. */
  color?: string;
  /** Accessible name. Pass an empty string to hide the logo from screen readers. */
  label?: string;
  className?: string;
}

const Lockup = styled.span<{ $color?: string; $gap: number }>`
  display: inline-flex;
  align-items: center;
  line-height: 0;
  /* An SVG's baseline is its bottom edge, so a block parent adds its strut descender below. */
  vertical-align: middle;
  gap: ${({ $gap }) => $gap}px;
  color: ${({ theme, $color }) => $color ?? theme.token.next.identity.brandBlue};
`;

export const RudderstackLogo: React.FC<RudderstackLogoProps> = ({
  variant = 'full',
  size = 20,
  color,
  label = 'RudderStack',
  className,
}) => {
  const isDecorative = label === '';
  const wordmarkHeight = Math.round(variant === 'full' ? size * LOCKUP_WORDMARK_SCALE : size);

  return (
    <Lockup
      className={className}
      $color={color}
      $gap={variant === 'full' ? Math.round(size * LOCKUP_GAP_SCALE) : 0}
      role={isDecorative ? undefined : 'img'}
      aria-label={isDecorative ? undefined : label}
      aria-hidden={isDecorative || undefined}
    >
      {variant !== 'wordmark' && (
        <RudderstackMark
          viewBox={MARK_INK_VIEW_BOX}
          height={size}
          width={Math.round(size * MARK_ASPECT_RATIO)}
        />
      )}
      {variant !== 'mark' && (
        <RudderstackWordmark
          height={wordmarkHeight}
          width={Math.round(wordmarkHeight * WORDMARK_ASPECT_RATIO)}
        />
      )}
    </Lockup>
  );
};
